/**
 * stepParamOverrides.js
 *
 * Merges user edits from the step cards back onto the proposed plan, and
 * derives the campaign budget review that gates campaign submission.
 *
 * Two different merges, deliberately:
 *  - `displayStepParams` keeps the raw override so a field the user is
 *    mid-edit shows exactly what they typed, including a blank box.
 *  - `planWithStepParamOverrides` drops blank overrides, so a cleared field
 *    means "unspecified" and falls back to the reviewed plan value instead
 *    of shipping an empty string the submission contract has to reject.
 */

import { getAction } from '../../../gibbs/registry';
import {
    prepareWorkflowSubmission,
    MULTI_PROTEIN_WORKSPACE,
    NO_TARGET_PROTEIN,
    UNATTRIBUTED_COMPOUND,
} from '../../../gibbs/autonomousCampaign/workflowSubmission';

/**
 * What to do about a refusal that is about the workspace's shape. Keyed by the
 * blocker the submission contract tags, because the two target-count refusals
 * call for opposite actions and a reviewer told to do the wrong one just gets
 * the same refusal back.
 *
 * Both target-count refusals now point at the panel's own Docking site selector
 * rather than sending the reviewer to another dialog: the decision is theirs, so
 * it belongs where they are reviewing. The selector writes the same target flag
 * as the `Is Target` checkbox on the Import Protein dialog's `Project / Targets`
 * tab, through the same action, so neither becomes a second source of truth.
 */
const SITE_PICKER = 'Use the Docking site selector above to choose which structure '
    + 'to dock into';
const WORKSPACE_SHAPE_REMEDIES = {
    [MULTI_PROTEIN_WORKSPACE]: `${SITE_PICKER}.`,
    [NO_TARGET_PROTEIN]: `${SITE_PICKER}.`,
    // No remedy is offered here on purpose. A compound that sits under no
    // structure is not something the reviewer chose, so inventing an action for
    // them would be worse than saying plainly that this needs looking at.
    [UNATTRIBUTED_COMPOUND]: 'A campaign cannot tell which structure those '
        + 'compounds would be docked into, so it will not dock them. Report this '
        + 'workspace to the maintainer.',
};

function isBlank(value) {
    return value === null || value === undefined
        || (typeof value === 'string' && value.trim() === '');
}

/**
 * Coerce user-edited param values to their registry-declared types.
 * Form inputs always produce strings; without this, numeric params
 * like `poseCount` get shipped as "2" instead of 2 and the BFD
 * server rejects the payload with a type error.
 *
 * A blank value is not a value: it is dropped so the reviewed plan's own
 * param survives. Keeping it would leave `poseCount: ''` in the plan, which
 * fails the submission contract with no way back except reloading the page.
 */
export function coerceParamOverrides(actionId, overrides) {
    const actionDef = getAction(actionId);
    if (!actionDef || !overrides) return overrides;
    const paramDefs = [
        ...(actionDef.parameters || []),
        ...(actionDef.inputs || []),
    ];
    const typeByName = new Map();
    for (const p of paramDefs) typeByName.set(p.name, p.type);

    const coerced = {};
    for (const [k, v] of Object.entries(overrides)) {
        const t = typeByName.get(k);
        if (isBlank(v) && t !== 'string') {
            continue;
        }
        if (t === 'number' && typeof v === 'string') {
            const n = Number(v);
            coerced[k] = Number.isFinite(n) ? n : v;
        } else if (t === 'boolean' && typeof v === 'string') {
            coerced[k] = v === 'true' || v === '1';
        } else {
            coerced[k] = v;
        }
    }
    return coerced;
}

export function planWithStepParamOverrides(plan, stepParams = {}) {
    const reviewedPlan = JSON.parse(JSON.stringify(plan));
    Object.entries(stepParams).forEach(([idx, overrides]) => {
        if (!reviewedPlan.actions[idx]) return;
        const actionId = reviewedPlan.actions[idx].actionId;
        reviewedPlan.actions[idx].params = {
            ...reviewedPlan.actions[idx].params,
            ...coerceParamOverrides(actionId, overrides),
        };
    });
    return reviewedPlan;
}

/**
 * What the step editor should show: the reviewed plan's own params, with any
 * user override on top. Showing the overrides alone left every untouched
 * field blank, which invited the user to type over a value the plan had
 * already chosen — and for a canonical campaign that value is part of the
 * reviewed contract.
 *
 * @param {Object} action - plan action entry `{ actionId, params }`
 * @param {Object} [overrides] - this step's entry in `stepParams`
 */
export function displayStepParams(action, overrides) {
    return { ...(action?.params || {}), ...(overrides || {}) };
}

/**
 * Field-level range complaint for a numeric param, or null when it is fine.
 *
 * `<input type="number" min max>` only caps the spinner — a typed value outside
 * the range still lands in the field. Without this the only feedback is the
 * panel-level budget rejection, which names the problem but not the field that
 * caused it. Mirrors the Docking pane's own "Valid range" message so the same
 * parameter reads the same way in both places.
 *
 * Range only: a non-integer or a non-numeric entry is still caught by the
 * submission contract and reported through the budget review.
 *
 * @param {Object} paramDef - registry param definition
 * @param {*} value - the raw field value
 * @returns {string|null}
 */
export function paramRangeError(paramDef, value) {
    const { min, max } = paramDef?.constraints || {};
    if (min === undefined && max === undefined) return null;
    if (isBlank(value)) return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    if ((min !== undefined && n < min) || (max !== undefined && n > max)) {
        if (min !== undefined && max !== undefined) return `Valid range: ${min} - ${max}.`;
        return min !== undefined ? `Minimum: ${min}.` : `Maximum: ${max}.`;
    }
    return null;
}

/**
 * Derive the campaign step budget for the plan as currently reviewed.
 *
 * Returns the derivation when the review succeeds, and otherwise the reason
 * submission is blocked. The reason has to name the actual problem: a bare
 * "waiting" message never changes, so a rejected edit reads as a calculation
 * that is still running and the only escape the user can find is a reload.
 *
 * @param {Object} args
 * @param {Object|null} args.plan - the proposed campaign plan
 * @param {Object} [args.stepParams] - `{ [stepIndex]: { ...overrides } }`
 * @param {Object|null} args.workspaceSnapshot - masked Deputy snapshot
 * @returns {{ derivation: Object|null, blockedReason: string|null }}
 */
export function campaignBudgetReviewState({
    plan, stepParams = {}, workspaceSnapshot,
} = {}) {
    if (!plan) {
        return { derivation: null, blockedReason: null };
    }
    if (!workspaceSnapshot) {
        return {
            derivation: null,
            blockedReason: 'Reading the campaign workspace review… '
                + 'If this does not clear, the Autonomous Campaign Deputy is not connected.',
        };
    }
    try {
        const { maxStepsDerivation } = prepareWorkflowSubmission({
            plan: planWithStepParamOverrides(plan, stepParams),
            workspace: workspaceSnapshot,
        });
        return { derivation: maxStepsDerivation, blockedReason: null };
    } catch (err) {
        const message = err?.message || 'the reviewed workflow could not be checked';
        const remedy = WORKSPACE_SHAPE_REMEDIES[err?.campaignBlocker];
        if (remedy) {
            // Not a budget problem, so it must not read as one. Each remedy names
            // the control that actually changes the thing the refusal is about —
            // the Is Target checkbox on the Import Protein dialog's Project /
            // Targets tab — because "multiple targets" on its own left the
            // reviewer holding an internal string with nowhere to go.
            return { derivation: null, blockedReason: `${message}. ${remedy}` };
        }
        return {
            derivation: null,
            blockedReason: `Campaign budget review rejected this workflow: ${message}`,
        };
    }
}
